'use client'

import { Fragment } from 'react'
import { CheckIcon } from '@phosphor-icons/react/dist/ssr/Check'
import { Avatar } from '@skop-app/lib-front'
import { Message, Review } from '@skop-app/lib-domain/types'
import { ReviewStatus, ReviewType } from '@skop-app/lib-types'
import { useTranslations } from 'next-intl'

import { useUserContext } from '@/context'
import { getOptionModerationReasonLabel } from '@/features/property/option.moderation'
import { useReviewDecision } from '@/hooks'
import { getPeriodFromToday, getReviewMetadataValue } from '@/utils/reviews'

import { Divider } from '../..'
import { MessageMenu } from '../Menu'

const URL_PATTERN = /(https?:\/\/[^\s]+)/g

const renderMessageContent = (content: string | undefined) => {
  if (!content) return null

  const segments = content.split(URL_PATTERN)

  return segments.map((segment, index) => {
    if (!segment) return null

    if (/^https?:\/\/[^\s]+$/.test(segment)) {
      return (
        <a
          key={`${segment}-${index}`}
          href={segment}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all text-blue-600 underline underline-offset-2 hover:text-blue-800"
        >
          {segment}
        </a>
      )
    }

    return <Fragment key={`${index}-${segment}`}>{segment}</Fragment>
  })
}

export const MessageAvatar = ({ message: { createdAt, user } }) => {
  const t = useTranslations()

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm text-slate-400">
      <span className="font-medium text-slate-700">{user?.name}</span>
      <span className="self-start text-xs text-slate-300">
        {getPeriodFromToday(t, createdAt)}
      </span>
    </div>
  )
}

type MessageBoxProps = {
  review: Review
  isFirst: boolean
  message?: Message
  hideDivider?: boolean
}

export const MessageBox = ({
  review,
  message,
  isFirst,
  hideDivider,
}: MessageBoxProps) => {
  const t = useTranslations()
  const { user } = useUserContext()
  const { applyCreationReviewDecision, getReviewDecisionAvailability } =
    useReviewDecision()

  if (!message) return null

  const { status: currentStatus } = review
  const { id, message: content, userID } = message

  const handleReviewStatus = async (nextStatus) => {
    try {
      await applyCreationReviewDecision({ review, nextStatus })
    } catch {
      // Mutations report the failure; a rejected decision keeps the review open.
    }
  }

  const rejectLabel = t('Property.refuse-option-validation')
  const decisionAvailability = getReviewDecisionAvailability(review)
  const decisionReasonLabel = getOptionModerationReasonLabel(
    t,
    decisionAvailability.reasonCode === 'admin-required'
      ? undefined
      : decisionAvailability.reasonCode,
  )

  const displayValidation = isFirst && currentStatus === ReviewStatus.PENDING
  const isOwner = userID === user?.id
  const isOptionCreationReview =
    review.type === ReviewType.REVIEW_OPTION &&
    getReviewMetadataValue(review, 'isCreationReview') === 'true'
  const canRejectFromGenericThread = !isOptionCreationReview
  const displayMenu =
    currentStatus === ReviewStatus.PENDING &&
    (isFirst || isOwner) &&
    canRejectFromGenericThread

  return (
    <>
      <div className="flex w-full gap-3 py-2">
        <Avatar name={message.user?.name} className="shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
            <MessageAvatar message={message} />
            <div className="flex shrink-0 items-start gap-1 pt-0.5">
              {displayValidation && (
                <button
                  onClick={() => handleReviewStatus(ReviewStatus.ACCEPTED)}
                  className="group inline-flex size-8 items-center justify-center rounded-full transition hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                  title={decisionReasonLabel ?? t('Common.accept')}
                  disabled={!decisionAvailability.canDecide}
                >
                  <CheckIcon className="size-5 text-orange-500 group-hover:text-white" />
                </button>
              )}
              {displayMenu && (
                <MessageMenu
                  t={t}
                  isFirst={isFirst}
                  isOwner={isOwner}
                  status={currentStatus}
                  canReviewDecision={decisionAvailability.canDecide}
                  reviewDecisionReasonLabel={decisionReasonLabel}
                  rejectLabel={rejectLabel}
                  canRejectDecision={canRejectFromGenericThread}
                  handleReviewStatus={handleReviewStatus}
                />
              )}
            </div>
          </div>
          <div
            key={id}
            className="whitespace-pre-wrap break-words text-sm text-slate-700"
          >
            {renderMessageContent(content)}
          </div>
        </div>
      </div>
      {!hideDivider && <Divider />}
    </>
  )
}
